/* eslint-disable camelcase */
import React from 'react';

import { margin, useClassnames } from '@lumapps/classnames';
import { DATA_LISTING_SEPARATOR } from '@lumapps/constants';
import { TextList, TextListProps } from '@lumapps/lumx-texts/components/TextList';
import { Typography, Text, ColorVariant, Theme, ColorPalette } from '@lumapps/lumx/react';

import { BaseUserField } from '../../types';
import { UserDetailedField, UserDetailedFieldProps } from '../UserDetailedField';

export interface UserDetailedFieldsProps extends Omit<TextListProps, 'children' | 'as'> {
    /**
     * Array of user fields to display.
     */
    fields: BaseUserField[];
    /**
     * The theme variant for the fields.
     * @default Theme.light
     */
    theme?: Theme;
    /** additional props for the user detailed field component */
    getAdditionalUserDetailedFieldProps?: (field: BaseUserField) => Partial<UserDetailedFieldProps>;
}

const CLASSNAME = 'user-detailed-fields';

/**
 * Displays a list of user fields with icons and values.
 * Renders each field using the UserDetailedField component.
 */
export const UserDetailedFields: React.FC<UserDetailedFieldsProps> = ({
    fields,
    theme = Theme.light,
    getAdditionalUserDetailedFieldProps,
    ...props
}) => {
    const { block, element } = useClassnames(CLASSNAME);
    const color = theme === Theme.light ? ColorPalette.dark : ColorPalette.light;

    return (
        <TextList as="ul" listStyle="none" {...props} className={block([props.className])}>
            {fields.map((field) => {
                const { values, id, iconClass, value, name } = field;
                let valueToDisplay: React.ReactNode = value;

                if (values) {
                    const definedValues = values.filter(Boolean);

                    if (definedValues.length > 1) {
                        valueToDisplay = (
                            <Text
                                as="p"
                                typography={Typography.body1}
                                color={color}
                                colorVariant={ColorVariant.L2}
                                className={element('values')}
                            >
                                {/* Not using InlineList since it does not work well with truncated values */}
                                {definedValues.filter(Boolean).join(DATA_LISTING_SEPARATOR)}
                            </Text>
                        );
                    } else {
                        // eslint-disable-next-line prefer-destructuring
                        valueToDisplay = definedValues[0];
                    }
                }

                if (valueToDisplay === undefined || valueToDisplay === null) {
                    return null;
                }

                const additionalProps = getAdditionalUserDetailedFieldProps
                    ? getAdditionalUserDetailedFieldProps(field)
                    : undefined;

                return (
                    <TextList.Item key={id} className={element('item', [margin('top', 'tiny')])}>
                        <UserDetailedField
                            color={color}
                            iconLabel={name}
                            value={valueToDisplay}
                            icon={iconClass}
                            label={value || values?.join() || ''}
                            {...additionalProps}
                            className={element('field', [additionalProps && additionalProps.className])}
                        />
                    </TextList.Item>
                );
            })}
        </TextList>
    );
};
